// Phase 129 — Target leading reticle. When the local player aims (within an IFF cone)
// at a moving enemy AND the held weapon is a slow projectile (spinfusor / mortar /
// other listed projectile weapons), project a small "lead" marker showing roughly where
// to aim so the projectile intercepts the enemy. Subtle indicator, not full lock-on.
import { useEffect, useState, type CSSProperties } from 'react';

type Vec3Like = { x?: number; y?: number; z?: number };

type LeadPlayer = {
  id?: string;
  team?: string | null;
  position?: Vec3Like;
  velocity?: Vec3Like;
};

type LeadReticleProps = {
  localPlayer: LeadPlayer & { yaw?: number; pitch?: number; selectedWeapon?: string | null };
  remotePlayers: Record<string, LeadPlayer>;
};

// Phase 129 — projectile speeds for lead computation. Slow projectiles benefit most.
const PROJECTILE_SPEEDS: Record<string, number> = {
  spinfusor: 85,
  mortar: 50,
  blaster: 110,
  frag: 44,
  concussion: 40,
};

function dot(ax: number, ay: number, az: number, bx: number, by: number, bz: number) {
  return ax * bx + ay * by + az * bz;
}

export function LeadReticle({ localPlayer, remotePlayers }: LeadReticleProps) {
  const [marker, setMarker] = useState<{ x: number; y: number; tone: string } | null>(null);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1280,
    h: typeof window !== 'undefined' ? window.innerHeight : 720,
  }));

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const weapon = localPlayer.selectedWeapon ?? '';
    const projectileSpeed = PROJECTILE_SPEEDS[weapon] ?? 0;
    if (!projectileSpeed || projectileSpeed >= 200) {
      setMarker(null);
      return;
    }
    const localTeam = (localPlayer.team ?? '').toLowerCase();
    const lpx = localPlayer.position?.x ?? 0;
    const lpy = (localPlayer.position?.y ?? 0) + 1.6;
    const lpz = localPlayer.position?.z ?? 0;
    const yaw = localPlayer.yaw ?? 0;
    const pitch = localPlayer.pitch ?? 0;
    const cosP = Math.cos(pitch);
    const fx = Math.sin(yaw) * cosP;
    const fy = Math.sin(pitch);
    const fz = -Math.cos(yaw) * cosP;
    const fovHalf = Math.cos((Math.PI / 180) * 8);

    let best: { sx: number; sy: number; align: number } | null = null;
    for (const p of Object.values(remotePlayers)) {
      if (!p.position || p.position.x == null || p.position.z == null) continue;
      const team = (p.team ?? '').toLowerCase();
      if (!team || team === localTeam) continue;
      const dx = (p.position.x ?? 0) - lpx;
      const dy = (p.position.y ?? 0) + 0.8 - lpy;
      const dz = (p.position.z ?? 0) - lpz;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist < 6 || dist > 200) continue;
      const inv = 1 / dist;
      const align = dot(fx, fy, fz, dx * inv, dy * inv, dz * inv);
      if (align < fovHalf) continue;

      // Simple lead: time-to-target = distance / projectileSpeed (first order).
      const tTarget = dist / projectileSpeed;
      const vx = p.velocity?.x ?? 0;
      const vy = p.velocity?.y ?? 0;
      const vz = p.velocity?.z ?? 0;
      const enemySpeed = Math.hypot(vx, vy, vz);
      if (enemySpeed < 3) {
        // Stationary — skip lead reticle, regular crosshair is fine.
        continue;
      }
      const leadX = (p.position.x ?? 0) + vx * tTarget - lpx;
      const leadY = (p.position.y ?? 0) + 0.8 + vy * tTarget - lpy;
      const leadZ = (p.position.z ?? 0) + vz * tTarget - lpz;

      // Project lead point onto the screen using the same yaw/pitch camera convention.
      const rx = Math.cos(yaw);
      const rz = Math.sin(yaw);
      const horiz = leadX * rx + leadZ * rz;
      const vert = leadY * Math.cos(pitch) - (Math.sin(pitch) * (leadX * Math.sin(yaw) + leadZ * -Math.cos(yaw)));
      const halfFovX = Math.tan((75 * Math.PI / 180) / 2);
      const aspect = viewport.w / viewport.h;
      const halfFovY = halfFovX / aspect;
      const forwardDist = Math.max(0.5, leadX * fx + leadY * fy + leadZ * fz);
      const sx = (horiz / forwardDist) / halfFovX;
      const sy = (vert / forwardDist) / halfFovY;
      const screenX = (sx * 0.5 + 0.5) * viewport.w;
      const screenY = (-sy * 0.5 + 0.5) * viewport.h;
      if (!best || align > best.align) {
        best = { sx: screenX, sy: screenY, align };
      }
    }
    if (!best) {
      setMarker(null);
      return;
    }
    setMarker({ x: best.sx, y: best.sy, tone: '#ffd966' });
  }, [
    localPlayer.position?.x,
    localPlayer.position?.y,
    localPlayer.position?.z,
    localPlayer.yaw,
    localPlayer.pitch,
    localPlayer.team,
    localPlayer.selectedWeapon,
    remotePlayers,
    viewport.w,
    viewport.h,
  ]);

  if (!marker) return null;

  const style: CSSProperties = {
    position: 'fixed',
    left: marker.x,
    top: marker.y,
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
    zIndex: 59,
  };

  return (
    <div style={style} aria-hidden>
      <svg width={28} height={28} style={{ overflow: 'visible' }}>
        <circle cx={14} cy={14} r={7} stroke={marker.tone} strokeWidth={1.4} fill="none" opacity={0.85} />
        <line x1={14} y1={2} x2={14} y2={6} stroke={marker.tone} strokeWidth={1.4} opacity={0.85} />
        <line x1={14} y1={22} x2={14} y2={26} stroke={marker.tone} strokeWidth={1.4} opacity={0.85} />
        <line x1={2} y1={14} x2={6} y2={14} stroke={marker.tone} strokeWidth={1.4} opacity={0.85} />
        <line x1={22} y1={14} x2={26} y2={14} stroke={marker.tone} strokeWidth={1.4} opacity={0.85} />
      </svg>
    </div>
  );
}
